import { useEffect, useMemo, useState } from "react";

import { XMarkIcon } from "@heroicons/react/20/solid";
import { LinkIcon } from "@heroicons/react/24/solid";

import classNames from "../../../utils/classNames";
import SearchPaletteDialog from "./SearchPaletteDialog";
import SearchPaletteTypePill from "./SearchPaletteTypePill";
import {
  SearchPaletteItem,
  SearchPaletteItemType,
} from "./SearchPaletteTypes";

interface SearchPaletteMultiSelectInputProps {
  label: string;
  placeholder?: string;
  theme?: "light" | "dark";
  type?: SearchPaletteItemType;
  allowedTypes?: SearchPaletteItemType[];
  items?: SearchPaletteItem[] | null;
  selectedItems?: SearchPaletteItem[];
  onChange?: (items: SearchPaletteItem[]) => void;
}

export default function SearchPaletteMultiSelectInput({
  label,
  placeholder = "Search...",
  theme = "light",
  type,
  allowedTypes,
  items = null,
  selectedItems: selectedItemsProp,
  onChange,
}: SearchPaletteMultiSelectInputProps) {
  const [open, setOpen] = useState(false);
  const [selectedItems, setSelectedItems] = useState<SearchPaletteItem[]>([]);

  useEffect(() => {
    if (selectedItemsProp == null) return;
    setSelectedItems(selectedItemsProp);
  }, [selectedItemsProp]);

  const typeLabels: Record<SearchPaletteItemType, string> = {
    Vehicle: "vehicles",
    Order: "orders",
    Stock: "stock",
    Lead: "leads",
    User: "users",
  };

  const selectedMap = useMemo(
    () => new Set(selectedItems.map((item) => String(item.id))),
    [selectedItems]
  );

  const mode = {
    opener:
      theme === "dark"
        ? "bg-gray-900 text-gray-200 border-gray-700"
        : "bg-white text-gray-600 border-gray-300",
    icon: theme === "dark" ? "text-primary-300" : "text-primary-500",
    insetPanel:
      theme === "dark"
        ? "bg-gray-800/70 border-gray-700"
        : "bg-gray-50 border-gray-200",
    cta:
      theme === "dark"
        ? "bg-primary-500 hover:bg-primary-400 text-white"
        : "bg-primary-600 hover:bg-primary-500 text-white",
    chip:
      theme === "dark"
        ? "bg-gray-800 text-gray-200 ring-gray-700"
        : "bg-gray-100 text-gray-800 ring-gray-200",
  };

  const fallbackTypes: SearchPaletteItemType[] = ["Order", "Vehicle", "Stock", "Lead"];
  const allowedTypeLabels = (allowedTypes?.length ? allowedTypes : fallbackTypes)
    .map((x) => typeLabels[x]);
  const helperText = type
    ? `Search and attach ${typeLabels[type]}`
    : `Search and attach ${allowedTypeLabels.join(", ")}`;
  const callToAction = type ? `Select linked ${typeLabels[type]}` : "Select linked items";

  const addSelected = (item: SearchPaletteItem) => {
    if (selectedMap.has(String(item.id))) return;
    const next = [...selectedItems, item];
    setSelectedItems(next);
    onChange?.(next);
  };

  const removeSelected = (id: string | number) => {
    const next = selectedItems.filter((item) => String(item.id) !== String(id));
    setSelectedItems(next);
    onChange?.(next);
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-500 mb-2">{label}</label>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={classNames(
          "w-full rounded-md border text-left text-sm focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500",
          mode.opener
        )}
      >
        <div className="flex items-center gap-2 px-3 py-2">
          <LinkIcon className={classNames("h-4 w-4 shrink-0", mode.icon)} />
          <span className="truncate">{helperText}</span>
        </div>
        <div className="px-3 pb-3">
          <div
            className={classNames(
              "rounded-md border border-dashed px-3 py-4 flex items-center justify-center",
              mode.insetPanel
            )}
          >
            <span
              className={classNames(
                "inline-flex items-center gap-2 rounded-md px-4 py-2 font-medium transition-colors",
                mode.cta
              )}
            >
              <LinkIcon className="h-4 w-4" />
              {callToAction}
            </span>
          </div>
        </div>
      </button>

      {selectedItems.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {selectedItems.map((item) => (
            <span
              key={item.id}
              className={classNames(
                "group inline-flex h-9 max-w-full items-center gap-1.5 rounded-full border border-gray-200 bg-white pl-1.5 pr-1 text-sm shadow-sm transition-colors hover:border-gray-300",
                mode.chip
              )}
            >
              <SearchPaletteTypePill
                type={item.type ?? "Vehicle"}
                nested
                className="shrink-0"
              />

              <span className="min-w-0 max-w-[240px] truncate font-medium text-gray-900">
                {item.name}
              </span>
              {item.isNewlyAdded && (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-semibold text-green-700">
                  Added
                </span>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeSelected(item.id);
                }}
                className="ml-0.5 rounded-full p-1 text-gray-400 transition hover:bg-gray-100 hover:text-gray-600"
                aria-label={`Remove ${item.name}`}
              >
                <XMarkIcon className="h-3.5 w-3.5" />
              </button>
            </span>
          ))}
        </div>
      )}

      <SearchPaletteDialog
        open={open}
        onClose={() => setOpen(false)}
        placeholder={placeholder}
        theme={theme}
        type={type}
        allowedTypes={allowedTypes}
        items={items}
        onSelect={addSelected}
      />
    </div>
  );
}
